import React, { useState } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    useLegalDocuments,
    useCreateLegalDocument,
    useUpdateLegalDocument,
    useDeleteLegalDocument,
    usePublishLegalDocument,
    useSupersedeLegalDocument,
    useSeedLegalDocuments,
    useUploadLegalDocumentContent,
} from '../../hooks/legalDocuments';
import type {
    AcceptanceMode,
    CreateLegalDocumentDto,
    LegalDocument,
    LegalDocumentStatus,
    LegalDocumentType,
} from '../../types/types';
import { getAdminLocale } from '../../i18n/adminLocale';
import { t } from '../../i18n/t';

const TYPES: LegalDocumentType[] = [
    'terms',
    'privacy',
    'esign',
    'health_data',
    'team_sharing',
    'guardian',
    'wellness',
];
const MODES: AcceptanceMode[] = ['required', 'acknowledge', 'optional', 'view_only'];

const apiErrorText = (e: unknown, fallback: string): string => {
    if (axios.isAxiosError(e)) {
        const data = e.response?.data as { message?: string | string[] } | string | undefined;
        if (typeof data === 'string' && data.trim()) return data;
        if (data && typeof data === 'object' && data.message) {
            return Array.isArray(data.message) ? data.message.join('\n') : data.message;
        }
    }
    return fallback;
};

const statusColor = (status: LegalDocumentStatus): 'success' | 'warning' | 'default' => {
    if (status === 'active') return 'success';
    if (status === 'superseded') return 'warning';
    return 'default';
};

const toLocalInput = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const DashboardLegalDocuments = () => {
    const { data: documents = [], isLoading, refetch } = useLegalDocuments();
    const deleteMutation = useDeleteLegalDocument();
    const publishMutation = usePublishLegalDocument();
    const supersedeMutation = useSupersedeLegalDocument();
    const seedMutation = useSeedLegalDocuments();
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState<LegalDocument | null>(null);
    const [copyFrom, setCopyFrom] = useState<LegalDocument | null>(null);

    const run = async (action: () => Promise<unknown>, fallback: string) => {
        try {
            await action();
        } catch (e) {
            console.error(e);
            alert(apiErrorText(e, fallback));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('legalDocuments.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => { setCopyFrom(null); setCreateOpen(true); }}>
                    {t('legalDocuments.new')}
                </Button>
                <Button
                    variant="outlined"
                    disabled={seedMutation.isPending}
                    onClick={() => {
                        if (!window.confirm(t('legalDocuments.seedConfirm'))) return;
                        run(() => seedMutation.mutateAsync(), t('legalDocuments.seedFailed'));
                    }}
                >
                    {t('legalDocuments.seed')}
                </Button>
                <Tooltip title={t('legalDocuments.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('legalDocuments.colCode')}</TableCell>
                            <TableCell>{t('legalDocuments.colVersion')}</TableCell>
                            <TableCell>{t('legalDocuments.colStatus')}</TableCell>
                            <TableCell>{t('legalDocuments.colMode')}</TableCell>
                            <TableCell>{t('legalDocuments.colTitle')}</TableCell>
                            <TableCell>{t('legalDocuments.colEffective')}</TableCell>
                            <TableCell align="right">{t('legalDocuments.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {documents.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    {t('legalDocuments.empty')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            documents.map((doc) => (
                                <TableRow key={doc.id}>
                                    <TableCell>{doc.code}</TableCell>
                                    <TableCell>{doc.version}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={t(`legalDocuments.status.${doc.status}`)}
                                            color={statusColor(doc.status)}
                                        />
                                    </TableCell>
                                    <TableCell>{t(`legalDocuments.mode.${doc.acceptanceMode}`)}</TableCell>
                                    <TableCell>{doc.locales?.en?.title || doc.title}</TableCell>
                                    <TableCell>{doc.effectiveAt ? new Date(doc.effectiveAt).toLocaleString() : '—'}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton size="small" onClick={() => setEditing(doc)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('legalDocuments.newVersion')}>
                                            <IconButton size="small" onClick={() => { setCopyFrom(doc); setCreateOpen(true); }}>
                                                <ContentCopyIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {doc.status !== 'active' && (
                                            <Tooltip title={t('legalDocuments.publish')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (!window.confirm(t('legalDocuments.publishConfirm', { code: doc.code, version: doc.version }))) return;
                                                        run(() => publishMutation.mutateAsync(doc.id), t('legalDocuments.publishFailed'));
                                                    }}
                                                >
                                                    <PublishIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {doc.status !== 'superseded' && (
                                            <Tooltip title={t('legalDocuments.supersede')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (!window.confirm(t('legalDocuments.supersedeConfirm', { code: doc.code, version: doc.version }))) return;
                                                        run(() => supersedeMutation.mutateAsync(doc.id), t('legalDocuments.supersedeFailed'));
                                                    }}
                                                >
                                                    <UnpublishedIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {doc.status === 'draft' && (
                                            <Tooltip title={t('common.delete')}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        if (!window.confirm(t('legalDocuments.deleteConfirm', { code: doc.code, version: doc.version }))) return;
                                                        run(() => deleteMutation.mutateAsync(doc.id), t('legalDocuments.deleteFailed'));
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {createOpen && (
                <LegalDocumentFormDialog
                    title={copyFrom ? t('legalDocuments.dialogVersion') : t('legalDocuments.dialogNew')}
                    copyFrom={copyFrom ?? undefined}
                    onClose={() => { setCreateOpen(false); setCopyFrom(null); }}
                />
            )}
            {editing && (
                <LegalDocumentFormDialog
                    title={t('legalDocuments.dialogEdit')}
                    document={editing}
                    onClose={() => setEditing(null)}
                />
            )}
        </Box>
    );
};

interface LegalDocumentFormDialogProps {
    title: string;
    document?: LegalDocument;
    copyFrom?: LegalDocument;
    onClose: () => void;
}

const LegalDocumentFormDialog: React.FC<LegalDocumentFormDialogProps> = ({
    title,
    document,
    copyFrom,
    onClose,
}) => {
    const source = document ?? copyFrom;
    const isEdit = document != null;
    const contentLocked = isEdit && document.status !== 'draft';
    const createMutation = useCreateLegalDocument();
    const updateMutation = useUpdateLegalDocument();
    const uploadMutation = useUploadLegalDocumentContent();

    const [code, setCode] = useState(source?.code ?? '');
    const [version, setVersion] = useState(isEdit ? source?.version ?? '' : '');
    const [type, setType] = useState<LegalDocumentType>(source?.type ?? 'terms');
    const [acceptanceMode, setAcceptanceMode] = useState<AcceptanceMode>(source?.acceptanceMode ?? 'required');
    const [requiresReacceptance, setRequiresReacceptance] = useState(source?.requiresReacceptance ?? false);
    const [effectiveAt, setEffectiveAt] = useState(toLocalInput(source?.effectiveAt));
    const [dataJson, setDataJson] = useState(JSON.stringify(source?.data ?? {}, null, 2));
    const [enTitle, setEnTitle] = useState(source?.locales?.en?.title ?? source?.title ?? '');
    const [enUrl, setEnUrl] = useState(contentLocked ? source?.locales?.en?.contentUrl ?? '' : (isEdit ? source?.locales?.en?.contentUrl ?? '' : ''));
    const [ruTitle, setRuTitle] = useState(source?.locales?.ru?.title ?? '');
    const [ruUrl, setRuUrl] = useState(contentLocked || isEdit ? source?.locales?.ru?.contentUrl ?? '' : '');
    const [enFile, setEnFile] = useState<File | null>(null);
    const [ruFile, setRuFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const showRussian = getAdminLocale() !== 'en';

    const handleSave = async () => {
        const trimmedCode = code.trim();
        const trimmedVersion = version.trim();
        if (!trimmedCode) {
            alert(t('legalDocuments.enterCode'));
            return;
        }
        if (!trimmedVersion) {
            alert(t('legalDocuments.enterVersion'));
            return;
        }
        let data: Record<string, unknown> = {};
        try {
            data = dataJson.trim() ? JSON.parse(dataJson) : {};
            if (typeof data !== 'object' || data === null || Array.isArray(data)) throw new Error('object');
        } catch {
            alert(t('legalDocuments.invalidData'));
            return;
        }

        const enContentUrl = enFile
            ? (isEdit ? (source?.locales?.en?.contentUrl ?? '') : '')
            : (contentLocked ? (source?.locales?.en?.contentUrl ?? '') : enUrl.trim());
        const ruContentUrl = ruFile
            ? (isEdit ? (source?.locales?.ru?.contentUrl ?? '') : '')
            : (contentLocked ? (source?.locales?.ru?.contentUrl ?? '') : ruUrl.trim());
        const locales: CreateLegalDocumentDto['locales'] = {
            en: { title: enTitle.trim(), contentUrl: enContentUrl },
        };
        if (showRussian && (ruTitle.trim() || ruContentUrl || ruFile || source?.locales?.ru)) {
            locales.ru = { title: ruTitle.trim(), contentUrl: ruContentUrl };
        } else if (!showRussian && isEdit && source?.locales?.ru) {
            locales.ru = {
                title: source.locales.ru.title ?? '',
                contentUrl: source.locales.ru.contentUrl ?? '',
            };
        }

        const dto: CreateLegalDocumentDto = {
            code: trimmedCode,
            type,
            version: trimmedVersion,
            effectiveAt: effectiveAt ? new Date(effectiveAt).toISOString() : null,
            requiresReacceptance,
            acceptanceMode,
            data,
            locales,
        };

        setSaving(true);
        try {
            let savedId = document?.id;
            if (isEdit && document) {
                await updateMutation.mutateAsync({ id: document.id, dto });
            } else {
                const created = await createMutation.mutateAsync(dto);
                savedId = created.id;
            }
            if (savedId != null && !contentLocked) {
                if (enFile) await uploadMutation.mutateAsync({ id: savedId, locale: 'en', file: enFile });
                if (showRussian && ruFile) await uploadMutation.mutateAsync({ id: savedId, locale: 'ru', file: ruFile });
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(apiErrorText(e, t('legalDocuments.saveFailed')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            label={t('legalDocuments.fieldCode')}
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            size="small"
                            disabled={isEdit || copyFrom != null}
                        />
                        <TextField
                            label={t('legalDocuments.fieldVersion')}
                            value={version}
                            onChange={(e) => setVersion(e.target.value)}
                            size="small"
                            disabled={contentLocked}
                        />
                        <TextField
                            select
                            label={t('legalDocuments.fieldType')}
                            value={type}
                            onChange={(e) => setType(e.target.value as LegalDocumentType)}
                            size="small"
                        >
                            {TYPES.map((value) => (
                                <MenuItem key={value} value={value}>{t(`legalDocuments.type.${value}`)}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={t('legalDocuments.fieldMode')}
                            value={acceptanceMode}
                            onChange={(e) => setAcceptanceMode(e.target.value as AcceptanceMode)}
                            size="small"
                        >
                            {MODES.map((value) => (
                                <MenuItem key={value} value={value}>{t(`legalDocuments.mode.${value}`)}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={requiresReacceptance}
                                onChange={(e) => setRequiresReacceptance(e.target.checked)}
                            />
                        }
                        label={t('legalDocuments.fieldReacceptance')}
                    />
                    <TextField
                        label={t('legalDocuments.fieldEffective')}
                        type="datetime-local"
                        value={effectiveAt}
                        onChange={(e) => setEffectiveAt(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <Typography variant="subtitle2">{t('legalDocuments.localeEn')}</Typography>
                    <TextField
                        label={t('legalDocuments.fieldTitle')}
                        value={enTitle}
                        onChange={(e) => setEnTitle(e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('legalDocuments.fieldContentUrl')}
                        value={enUrl}
                        onChange={(e) => setEnUrl(e.target.value)}
                        size="small"
                        disabled={contentLocked}
                    />
                    {!contentLocked && (
                        <Button variant="outlined" component="label">
                            {enFile ? enFile.name : t('legalDocuments.fieldHtml')}
                            <input
                                hidden
                                type="file"
                                accept=".html,.htm,text/html"
                                onChange={(e) => setEnFile(e.target.files?.[0] ?? null)}
                            />
                        </Button>
                    )}
                    {showRussian && (
                        <>
                            <Typography variant="subtitle2">{t('legalDocuments.localeRu')}</Typography>
                            <TextField
                                label={t('legalDocuments.fieldTitle')}
                                value={ruTitle}
                                onChange={(e) => setRuTitle(e.target.value)}
                                size="small"
                            />
                            <TextField
                                label={t('legalDocuments.fieldContentUrl')}
                                value={ruUrl}
                                onChange={(e) => setRuUrl(e.target.value)}
                                size="small"
                                disabled={contentLocked}
                            />
                            {!contentLocked && (
                                <Button variant="outlined" component="label">
                                    {ruFile ? ruFile.name : t('legalDocuments.fieldHtml')}
                                    <input
                                        hidden
                                        type="file"
                                        accept=".html,.htm,text/html"
                                        onChange={(e) => setRuFile(e.target.files?.[0] ?? null)}
                                    />
                                </Button>
                            )}
                        </>
                    )}
                    <TextField
                        label={t('legalDocuments.fieldData')}
                        value={dataJson}
                        onChange={(e) => setDataJson(e.target.value)}
                        multiline
                        minRows={3}
                        size="small"
                        placeholder="{}"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardLegalDocuments;
